import apiClient from '@/api/client';

export interface CompanyInGroup {
  id: string;
  name: string;
  short_code: string;
  colour: string;
  is_active: boolean;
  parent_id?: string;
  country_code?: string;
  base_currency?: string;
  // Leadership
  ceo_name?: string;
  ceo_title?: string;
  ceo_email?: string;
  ceo_phone?: string;
}

export interface IntercompanyTransaction {
  id: string;
  group_id: string;
  from_company_id: string;
  to_company_id: string;
  from_company_name?: string;
  to_company_name?: string;
  transaction_date: string;
  amount: string;
  description: string;
  reference?: string;
  transaction_type: string;
  status: string;
  created_by: string;
  confirmed_by?: string;
  confirmed_at?: string;
  voided: boolean;
  void_reason?: string;
  created_at: string;
}

export type ICTransactionType = 'transfer' | 'sale' | 'service' | 'loan';

export interface ICReconciliationItem {
  from_company_id: string;
  from_company_name: string;
  to_company_id: string;
  to_company_name: string;
  receivable_balance: string;
  payable_balance: string;
  difference: string;
  status: 'matched' | 'mismatch';
}

export interface ICReconciliationReport {
  period: string;
  total_ic_receivable: string;
  total_ic_payable: string;
  net_difference: string;
  items: ICReconciliationItem[];
  has_mismatches: boolean;
}

export interface WorkforceAnalytics {
  total_employees: number;
  total_active: number;
  total_probation: number;
  total_terminated_ytd: number;
  attrition_rate?: number;
  new_hires_ytd: number;
  avg_tenure_months?: number;
  avg_basic_salary?: number;
  total_monthly_payroll?: number;
  total_departments: number;
  gender_distribution?: { male: number; female: number; other: number };
  department_breakdown?: { name: string; count: number }[];
  work_location_distribution?: Record<string, number>;
  status_distribution?: Record<string, number>;
  subsidiaries: {
    company_id: string;
    company_name: string;
    colour: string;
    active: number;
    probation: number;
    headcount: number;
    terminated_ytd: number;
    new_hires_ytd: number;
    attrition_rate?: number;
    avg_tenure_months?: number;
    male: number;
    female: number;
    other_gender: number;
    department_count: number;
    avg_basic_salary?: number;
    monthly_payroll?: number;
  }[];
}

export interface SubsidiaryPerformance {
  company_id: string;
  company_name: string;
  colour: string;
  total_revenue: string;
  total_expenses: string;
  net_profit: string;
  profit_margin?: string;
  employee_count: number;
}

export interface GroupDashboardKPIs {
  period: string;
  total_revenue: string;
  total_expenses: string;
  net_profit: string;
  profit_margin?: string;
  total_employees: number;
  company_count: number;
  subsidiaries: SubsidiaryPerformance[];
  ic_revenue_eliminated?: string;
}

export interface ConsolidatedLineItem {
  account_code: string;
  account_name: string;
  account_type: string;
  companies: { company_id: string; company_name: string; amount: string }[];
  elimination: string;
  group_total: string;
}

/** A subsidiary whose own ledger does not balance; the consolidated figures exclude the difference. */
export interface UnbalancedCompany {
  company_id: string;
  company_name: string;
  currency: string;
  /** Decimal string, kept as a string (no float arithmetic). */
  imbalance: string;
}

/** Body of the consolidated P&L / balance sheet / trial balance responses. */
export interface ConsolidatedReportBody {
  lines?: ConsolidatedLineItem[];
  /** Optional: present only when at least one subsidiary's ledger does not balance. */
  unbalanced_companies?: UnbalancedCompany[];
}

export interface GroupInfo {
  id: string;
  name: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  lga?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  email?: string;
  phone?: string;
  website?: string;
  description?: string;
  logo_url?: string;
  color_palette: string;
  hiring_authority?: string;
  // Leadership
  ceo_name?: string;
  ceo_title?: string;
  ceo_email?: string;
  ceo_phone?: string;
  // Social media
  linkedin_url?: string;
  twitter_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  youtube_url?: string;
  headquarters_company_id?: string | null;
}

export interface CostAllocationRule {
  id: string;
  group_id: string;
  name: string;
  description?: string;
  method: string;
  allocations: Record<string, number>;
  is_active: boolean;
  created_at: string;
}

export interface ExchangeRateEntry {
  id: string;
  group_id: string;
  from_currency: string;
  to_currency: string;
  rate: string;
  effective_date: string;
  source: string;
  created_at?: string;
}

export interface GroupInvite {
  id: string;
  group_id: string;
  company_id: string;
  company_name?: string;
  group_name?: string;
  status: string;
  message?: string;
  expires_at: string;
  created_at: string;
}

export interface DepartmentMatrix {
  companies: { company_id: string; company_name: string; colour: string }[];
  departments: { name: string; total: number; counts: Record<string, number> }[];
}

export interface TaxCompanyLine {
  company_id: string;
  company_name: string;
  colour: string;
  due: string;
  paid: string;
  outstanding: string;
  overdue: number;
}

export interface TaxObligationLine {
  obligation_type: string;
  label: string;
  authority?: string;
  due: string;
  paid: string;
  outstanding: string;
  companies: TaxCompanyLine[];
}

export interface GroupTaxSummary {
  year: number;
  country: string;
  currency: string;
  totals: { due: string; paid: string; outstanding: string; overdue_count: number };
  obligations: TaxObligationLine[];
  companies: TaxCompanyLine[];
  filing_calendar: {
    code: string; name: string; frequency: string; due_description: string; authority?: string;
  }[];
  liabilities: {
    companies: {
      company_id: string;
      company_name: string;
      colour: string;
      vat_payable: string;
      wht_payable: string;
      cit_estimate: string;
      cit_exempt: boolean;
      cit_rate: string;
    }[];
    vat_total: string;
    wht_total: string;
    cit_total: string;
  };
}

export interface CompanyCreatePayload {
  name: string;
  short_code?: string;
  country_code: string;
  industry?: string;
  email?: string;
  colour?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  lga?: string;
  state?: string;
  postal_code?: string;
  ceo_name?: string;
  ceo_title?: string;
  ceo_email?: string;
  ceo_phone?: string;
}

export const groupApi = {
  // Companies
  listCompanies: () => apiClient.get<CompanyInGroup[]>('/group/companies'),
  createCompany: (data: CompanyCreatePayload) =>
    apiClient.post<CompanyInGroup>('/group/companies', data),
  // Pull an existing standalone company (one the caller administers) into the
  // current group; detach a company from the current group into its own.
  joinCompany: (companyId: string) =>
    apiClient.post<CompanyInGroup>('/group/companies/join', { company_id: companyId }),
  leaveCompany: (companyId: string) =>
    apiClient.post<CompanyInGroup>(`/group/companies/${companyId}/leave`),

  // Cross-owner group-join invites
  createInvite: (data: { company_id?: string; company_short_code?: string; message?: string }) =>
    apiClient.post<GroupInvite>('/group/invites', data),
  listOutgoingInvites: () => apiClient.get<GroupInvite[]>('/group/invites/outgoing'),
  listIncomingInvites: () => apiClient.get<GroupInvite[]>('/group/invites/incoming'),
  acceptInvite: (id: string) => apiClient.post<CompanyInGroup>(`/group/invites/${id}/accept`),
  declineInvite: (id: string) => apiClient.post<GroupInvite>(`/group/invites/${id}/decline`),
  cancelInvite: (id: string) => apiClient.post<GroupInvite>(`/group/invites/${id}/cancel`),

  // Intercompany
  listIntercompany: (year: number, status?: string) =>
    apiClient.get<IntercompanyTransaction[]>('/group/intercompany', {
      params: { year, ...(status ? { status } : {}) },
    }),
  createIntercompany: (data: {
    from_company_id: string;
    to_company_id: string;
    transaction_date: string;
    amount: number;
    description: string;
    reference?: string;
    transaction_type?: ICTransactionType;
  }) => apiClient.post<IntercompanyTransaction>('/group/intercompany', data),
  confirmIntercompany: (id: string) =>
    apiClient.post<IntercompanyTransaction>(`/group/intercompany/${id}/confirm`),
  cancelIntercompany: (id: string, reason: string) =>
    apiClient.post<IntercompanyTransaction>(`/group/intercompany/${id}/cancel`, { reason }),
  getICReconciliation: (year: number) =>
    apiClient.get<ICReconciliationReport>('/group/intercompany/reconciliation', { params: { year } }),

  // Consolidated Reports
  consolidatedPL: (year: number) =>
    apiClient.get('/group/consolidated/profit-loss', { params: { year } }),
  consolidatedBS: (year: number) =>
    apiClient.get('/group/consolidated/balance-sheet', { params: { year } }),
  consolidatedTB: (year: number) =>
    apiClient.get('/group/consolidated/trial-balance', { params: { year } }),
  // Post intercompany eliminations as an auditable journal entry (idempotent/year).
  postConsolidationEliminations: (year: number) =>
    apiClient.post<{
      posted: boolean; reason?: string; journal_entry_id?: string;
      lines?: number; residual?: string; year?: number; elimination_company_id?: string;
    }>('/group/consolidation/post-eliminations', null, { params: { year } }),

  // Dashboard
  dashboard: (year: number) =>
    apiClient.get<GroupDashboardKPIs>('/group/dashboard', { params: { year } }),

  // Cost Allocation Rules
  listAllocationRules: () =>
    apiClient.get<CostAllocationRule[]>('/group/allocation-rules'),
  createAllocationRule: (data: {
    name: string;
    description?: string;
    method: string;
    allocations: Record<string, number>;
  }) => apiClient.post<CostAllocationRule>('/group/allocation-rules', data),
  updateAllocationRule: (id: string, data: {
    name?: string;
    description?: string;
    method?: string;
    allocations?: Record<string, number>;
    is_active?: boolean;
  }) => apiClient.patch<CostAllocationRule>(`/group/allocation-rules/${id}`, data),
  deactivateAllocationRule: (id: string) =>
    apiClient.delete(`/group/allocation-rules/${id}`),
  applyAllocationRule: (data: {
    rule_id: string;
    total_amount: number;
    description: string;
    expense_account_code: string;
    transaction_date: string;
  }) => apiClient.post<{ status: string; transactions_created: number; transaction_ids: string[] }>(
    '/group/allocation-rules/apply', data
  ),

  // Group info
  getGroupInfo: () => apiClient.get<GroupInfo>('/group/info'),
  updateGroupInfo: (data: Partial<GroupInfo>) =>
    apiClient.patch<GroupInfo>('/group/info', data),
  uploadGroupLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ logo_url: string }>('/group/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  uploadGroupFavicon: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ favicon_url: string }>('/group/favicon', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  generateSEO: () =>
    apiClient.post<{ meta_title: string; meta_description: string; meta_keywords: string; companies_analyzed: number }>('/group/generate-seo'),

  // Workforce Analytics
  workforceAnalytics: (year: number) =>
    apiClient.get<WorkforceAnalytics>('/group/workforce-analytics', { params: { year } }),
  departmentMatrix: () =>
    apiClient.get<DepartmentMatrix>('/group/department-matrix'),

  // Tax & Compliance cockpit (read-only)
  taxCompliance: (year: number) =>
    apiClient.get<GroupTaxSummary>('/group/tax-compliance', { params: { year } }),

  // Exchange Rates (FX Consolidation)
  listExchangeRates: (params?: {
    from_currency?: string;
    to_currency?: string;
    year?: number;
  }) => apiClient.get<ExchangeRateEntry[]>('/group/exchange-rates', { params }),

  createExchangeRate: (data: {
    from_currency: string;
    to_currency: string;
    rate: number;
    effective_date: string;
    source?: string;
  }) => apiClient.post<ExchangeRateEntry>('/group/exchange-rates', data),

  deleteExchangeRate: (id: string) =>
    apiClient.delete(`/group/exchange-rates/${id}`),

  getGroupCurrencies: () =>
    apiClient.get<{
      currencies: { code: string; companies: string[] }[];
      is_multi_currency: boolean;
    }>('/group/exchange-rates/currencies'),
};
